/**
 * Statsig Event Utilities
 *
 * This file contains utility functions and helper classes for event tracking.
 */

import { envConfig } from "@configs/env";
import { StatsigUser } from "@statsig/client-core";
import { MemberModel } from "kz-ui-sdk";
import { EVENTS, EventName } from "./constants";
import { DepositStep, RegistrationStep } from "./types";

// ===== UTILITY FUNCTIONS =====

export function isValidEvent(eventName: string): eventName is EventName {
  return Object.values(EVENTS).includes(eventName as EventName);
}

export function isUserAction(eventName: string): boolean {
  return eventName.startsWith("act:");
}

export function isSystemEvent(eventName: string): boolean {
  return eventName.startsWith("evt:");
}

/**
 * Helper class to track deposit funnel with proper step progression
 */
export class DepositFunnelTracker {
  private static instance: DepositFunnelTracker;
  private depositStartTime: number | null = null;
  private completedSteps: DepositStep[] = [];
  private attemptCounts: Record<string, number> = {};
  private selectedData: Record<string, any> = {};

  static getInstance(): DepositFunnelTracker {
    if (!DepositFunnelTracker.instance) {
      DepositFunnelTracker.instance = new DepositFunnelTracker();
    }
    return DepositFunnelTracker.instance;
  }

  startDeposit(): void {
    this.depositStartTime = Date.now();
    this.completedSteps = [];
    this.attemptCounts = {};
    this.selectedData = {};
  }

  addStep(step: DepositStep): void {
    if (!this.completedSteps.includes(step)) {
      this.completedSteps.push(step);
    }
  }

  incrementAttempt(eventType: string): number {
    this.attemptCounts[eventType] = (this.attemptCounts[eventType] || 0) + 1;
    return this.attemptCounts[eventType];
  }

  setSelectedData(key: string, value: any): void {
    this.selectedData[key] = value;
  }

  getSelectedData(key: string): any {
    return this.selectedData[key];
  }

  getTotalTime(): number {
    return this.depositStartTime ? Date.now() - this.depositStartTime : 0;
  }

  getCompletedSteps(): DepositStep[] {
    return [...this.completedSteps];
  }

  reset(): void {
    this.depositStartTime = null;
    this.completedSteps = [];
    this.attemptCounts = {};
    this.selectedData = {};
  }
}

// ===== REGISTRATION ANALYTICS HELPER =====

/**
 * Helper class to track registration funnel with proper step progression
 */
export class RegistrationFunnelTracker {
  private static instance: RegistrationFunnelTracker;
  private registrationStartTime: number | null = null;
  private completedSteps: RegistrationStep[] = [];
  private attemptCounts: Record<string, number> = {};

  static getInstance(): RegistrationFunnelTracker {
    if (!RegistrationFunnelTracker.instance) {
      RegistrationFunnelTracker.instance = new RegistrationFunnelTracker();
    }
    return RegistrationFunnelTracker.instance;
  }

  startRegistration(): void {
    this.registrationStartTime = Date.now();
    this.completedSteps = [];
    this.attemptCounts = {};
  }

  addStep(step: RegistrationStep): void {
    if (!this.completedSteps.includes(step)) {
      this.completedSteps.push(step);
    }
  }

  incrementAttempt(eventType: string): number {
    this.attemptCounts[eventType] = (this.attemptCounts[eventType] || 0) + 1;
    return this.attemptCounts[eventType];
  }

  getTotalTime(): number {
    return this.registrationStartTime ? Date.now() - this.registrationStartTime : 0;
  }

  getCompletedSteps(): RegistrationStep[] {
    return [...this.completedSteps];
  }

  reset(): void {
    this.registrationStartTime = null;
    this.completedSteps = [];
    this.attemptCounts = {};
  }
}

// ===== BRAND GROUP MAPPING =====

const BRAND_GROUP_MAP: Record<string, string> = {
  kz: "kzg1",
  kz2: "kzg2",
  bl1: "blg1",
  wdb1: "wdb1",
  bk: "96g1",
  phk1: "phk1",
  idk1: "idk1",
  bdk1: "bdk1",
  pkk1: "pkk1",
};

export function mapBrandGroup(input: string | undefined | null): string | undefined {
  if (!input) return undefined;
  return BRAND_GROUP_MAP[input] ?? input;
}

export function buildUserProperties(member: MemberModel, serverPrefix: string): StatsigUser {
  return {
    userID: `${serverPrefix || ""}${member.apiIdentifier}` || member.id,
    email: member.email || undefined,
    custom: {
      memberId: member.id,
      accountId: member.accountId || envConfig?.accountId,
      group: mapBrandGroup(envConfig?.brand?.group),
      brand: envConfig?.brand?.key,
      phone: member.phone,
      fullname: member.fullname || undefined,
      ...(member.primaryWallet && {
        walletBalance: member.primaryWallet.balance?.toString(),
        walletId: member.primaryWallet.id,
      }),
      memberGroupId: member?.group?.groupId || undefined,
      memberGroupName: member?.group?.group?.name || undefined,
      appVersion: import.meta.env.VITE_APP_VERSION,
    },
    customIDs: {
      accountId: member.accountId || envConfig?.accountId,
      group: mapBrandGroup(envConfig?.brand?.group),
      brand: envConfig?.brand?.key,
    },
  };
}
